// ----------------------------------------------------------------------
// <copyright file="Izenda.FilterValueStore.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

import { FILTER_MODE } from 'logic/data/Izenda.FilterLookupConstants';
import { isEmpty, some, map, isNil, split, omit, toString, includes, first } from 'lodash/fp';
import { observable, computed } from 'mobx';
import fetcher from 'fetchers/Izenda.FilterValueDataFetcher';
import systemSettingfetcher from 'fetchers/Izenda.SystemSettingFetcher';
import { FILTER_TYPE } from 'logic/data/Izenda.QuerySourceTypeConfig';
import uiState from 'logic/Izenda.UiState.js';

export default class FilterValueStore {
  @observable
  filterMode: string = null;

  @observable
  connectionList: Array = [];

  @observable
  tableList: Array = [];

  @observable
  fieldList: Array = [];

  @observable
  displayValueList: Array = [];

  @observable
  dbStructureValue: Object = null;

  @observable
  userDefinedValue: string = null;

  @observable
  userDefinedOptions: Array = [];

  @computed
  get tenantId() {
    return uiState.getTenantId();
  }

  @computed
  get showPopup() {
    return !isNil(this.editingFilterValue) && this.querySourceDetailStore.isEditingFilter;
  }

  constructor(querySourceDetailStore) {
    this.querySourceDetailStore = querySourceDetailStore;
    this.firstLoad = true;
    this.editingQuerySourceField = querySourceDetailStore.editingQuerySourceField;
    this.editingFilterValue = this.editingQuerySourceField.getFilterValues();

    this.filterMode =
      !isNil(this.editingFilterValue) && toString(this.editingFilterValue.type) === FILTER_TYPE.USER_DEFINED
        ? FILTER_MODE.UserDefined
        : FILTER_MODE.DbStructure;

    this.dbStructureValue = {
      database:
        this.editingFilterValue && this.editingFilterValue.databaseId
          ? { key: this.editingFilterValue.databaseId, value: this.editingFilterValue.databaseName }
          : {},
      table:
        this.editingFilterValue && this.editingFilterValue.querySourceId
          ? { key: this.editingFilterValue.querySourceId, value: this.editingFilterValue.querySourceName }
          : {},
      lookupKey:
        this.editingFilterValue && this.editingFilterValue.lookupKeyQuerySourceFieldId
          ? {
              key: this.editingFilterValue.lookupKeyQuerySourceFieldId,
              value: this.editingFilterValue.lookupKeyQuerySourceFieldName
            }
          : {},
      displayValue:
        this.editingFilterValue && this.editingFilterValue.displayQuerySourceFieldId
          ? {
              key: this.editingFilterValue.displayQuerySourceFieldId,
              value: this.editingFilterValue.displayQuerySourceFieldName
            }
          : {}
    };
    this.userDefinedValue =
      this.editingFilterValue && this.editingFilterValue.userDefinedValues
        ? this.editingFilterValue.userDefinedValues.join(',')
        : '';

    this.setCurrentValueToOptions();
  }

  /**
   * Update Filter mode
   * @param {type} filterMode
   */
  updateFilterMode(filterMode: string) {
    this.filterMode = filterMode;
  }

  /**
   * Set value for dropdown
   * @param {Array<Object>} response
   * @param {Object} properties
   * @param {Function} functionSetData
   * @param {Function} functionsSetEmpty
   */
  setValueForDropDown(
    response: Array<any>,
    properties: any,
    functionSetData: Function,
    functionsSetEmpty: Array<Function>
  ) {
    const setEmptyData = () => {
      functionSetData.call(this, {});
      functionsSetEmpty.forEach(functionToSet => functionToSet.call(this, {}));
      return Promise.resolve();
    };

    if (!isEmpty(response)) {
      const allAvailableKeys = map('key', response);
      return properties.key
        ? includes(properties.key, allAvailableKeys)
          ? functionSetData.call(this, properties)
          : setEmptyData()
        : functionSetData.call(this, first(response));
    } else {
      return setEmptyData();
    }
  }

  /**
   * Get Database list
   */
  getDatabaseList() {
    return fetcher.getDatabaseList(this.tenantId).then(response => {
      this.connectionList = response;
      return this.setValueForDropDown(response, this.dbStructureValue.database, this.setDbName, [
        this.setTable,
        this.setLookupKey,
        this.setDisplayValue
      ]);
    });
  }

  /**
   * Set current value to options
   */
  setCurrentValueToOptions() {
    if (this.userDefinedValue) {
      const userDefineValueList = map(value => {
        return {
          name: value,
          id: value,
          isNew: true
        };
      }, split(',', this.userDefinedValue));

      userDefineValueList.forEach(value => this.addNewFilterValue(value));
    }
  }

  /**
   * Get system variables
   */
  getSystemVariables() {
    return systemSettingfetcher.getSystemVariables().then(response => {
      if (!isEmpty(response)) {
        response.forEach(option =>
          this.userDefinedOptions.push({
            name: option,
            id: option,
            isNew: false
          })
        );
      }
    });
  }

  /**
   * Get table list
   * @param {string} connectionId The connection id
   */
  getTableList(connectionId: string) {
    return fetcher.getTableList(connectionId).then(response => {
      this.tableList = response;
      return this.setValueForDropDown(response, this.dbStructureValue.table, this.setTable, [
        this.setLookupKey,
        this.setDisplayValue
      ]);
    });
  }

  /**
   * Get field list
   * @param {string} tableId The table id
   */
  getFieldList(tableId: string) {
    return fetcher.getFieldList(tableId).then(response => {
      this.fieldList = response;

      this.setValueForDropDown(response, this.dbStructureValue.lookupKey, this.setLookupKey, []);
      this.setValueForDropDown(response, this.dbStructureValue.displayValue, this.setDisplayValue, []);

      this.firstLoad = false;
    });
  }

  /**
   * Set DB Name after get list database
   * @param {any} newValue Contains info of the new db
   */
  setDbName(newValue: any) {
    this.dbStructureValue.database = newValue;
    if (!this.firstLoad) this.dbStructureValue.table = {};
    this.tableList = [];
    if (newValue.key) {
      return this.getTableList(newValue.key);
    }
  }

  /**
   * Set Table after get list table
   * @param {any} newValue Contains info of the new table
   */
  setTable(newValue: any) {
    this.dbStructureValue.table = newValue;
    if (!this.firstLoad) {
      this.dbStructureValue.lookupKey = {};
      this.dbStructureValue.displayValue = {};
    }
    this.fieldList = [];
    if (newValue.key) {
      return this.getFieldList(newValue.key);
    }
  }

  /**
   * Set Lookup key value
   * @param {string} newValue New lookup key
   */
  setLookupKey(newValue: string) {
    this.dbStructureValue.lookupKey = newValue;
  }

  /**
   * Set display value
   * @param {string} newValue New display value
   */
  setDisplayValue(newValue: string) {
    this.dbStructureValue.displayValue = newValue;
  }

  /**
   * Save filter value
   */
  saveFillerValue() {
    this.editingQuerySourceField.setFilterValues(this.getFilterValue());
    this.editingQuerySourceField.setValidFilterLookup();
  }

  /**
   * Get Filter value
   */
  getFilterValue() {
    if (this.filterMode === FILTER_MODE.DbStructure) {
      return {
        type: FILTER_TYPE.DB_STRUCTURE,
        databaseName: this.dbStructureValue.database.value,
        databaseId: this.dbStructureValue.database.key,
        querySourceName: this.dbStructureValue.table.value,
        querySourceId: this.dbStructureValue.table.key,
        lookupKeyQuerySourceFieldName: this.dbStructureValue.lookupKey.value,
        lookupKeyQuerySourceFieldId: this.dbStructureValue.lookupKey.key,
        displayQuerySourceFieldName: this.dbStructureValue.displayValue.value,
        displayQuerySourceFieldId: this.dbStructureValue.displayValue.key,
        userDefinedValues: []
      };
    } else {
      return {
        type: FILTER_TYPE.USER_DEFINED,
        userDefinedValues: split(',', this.userDefinedValue)
      };
    }
  }

  /**
   * Set user defined value
   * @param {string} value User defined value
   */
  setUserDefinedFilterValue(value: string) {
    this.userDefinedValue = value;
  }

  /**
   * Reset popup
   */
  resetPopup() {
    this.querySourceDetailStore.editingQuerySourceField = null;
    this.querySourceDetailStore.isEditingFilter = false;
  }

  /**
   * Add new filter value
   * @param {any} newValue New filter value
   */
  addNewFilterValue(newValue) {
    if (!some(omit(['isNew'], newValue), this.userDefinedOptions)) {
      this.userDefinedOptions.push(newValue);
    }
  }
}



// WEBPACK FOOTER //
// ./ui/logic/Izenda.FilterValueStore.js